/**
 * Hyperframes port of `src/scenes/text/kineticHook.tsx`.
 *
 * Per-word entrance with three variants (scale | blur | rotate). The
 * Remotion version uses `brandSpring` + interpolate; here we approximate
 * with a 0.73s ease-out tween — visually equivalent for the user-facing
 * "punchy" feel (parity is for shape & timing, not for spring physics).
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import { escapeHtml } from "../../composition.js";
import { typeSize, accentFor, makeTl, clipOutroFade, combineSceneFragments, weights, withAlpha } from "../../reel/sceneShared.js";
import { tl } from "../../timeline.js";

const STAGGER_SEC = 4 / 30;

export const kineticHook: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const text = inputs.text ?? "";
  const variant = inputs.variant ?? "scale";
  const accent = accentFor(inputs);
  const words = text.split(/\s+/).filter(Boolean);
  const t = makeTl(clipStartSec);

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, {
    variant: variant === "rotate" ? "neon" : "cinematic",
    accent,
  });

  const wrapId = `${clipPrefix}-wrap`;
  const maxWidth = ctx.format === "9:16" ? 920 : 1500;

  // Dynamische Schriftgrößen-Berechnung: das längste Wort MUSS in die
  // verfügbare Zeilenbreite passen — sonst läuft z. B. "Unterstützen." rechts
  // aus dem Bild und der Punkt verschwindet. Wir starten bei `hero`, leiten
  // aber daraus eine harte Obergrenze pro Wortlänge ab.
  const heroBase = typeSize("hero", ctx.format);
  const longestWordLen = Math.max(...words.map((w) => w.length), 1);
  // Outfit Black ist mit `letter-spacing: -3.5px` recht eng — empirisch
  // passen ca. 0.58em pro Zeichen plus etwas Sicherheitspuffer.
  const usableWidth = maxWidth - 48;
  const maxByWidth = usableWidth / (longestWordLen * 0.58);
  const heroSize = Math.min(heroBase, maxByWidth);
  const accentIdx = Math.floor(words.length / 2);

  const wordSpans = words
    .map((w, i) => {
      const isAccent = i === accentIdx;
      const colorStyle = isAccent
        ? `color:${accent};text-shadow:0 0 32px ${withAlpha(accent, 0.5)};`
        : "";
      return `<span class="kh-w" id="${clipPrefix}-w-${i}" style="display:inline-block;margin-right:.25em;will-change:transform,opacity;${colorStyle}">${escapeHtml(w)}</span>`;
    })
    .join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:96px;">
  <h1 style="margin:0;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${heroSize}px;line-height:0.95;letter-spacing:-3.5px;text-align:center;text-wrap:balance;max-width:${maxWidth}px;">${wordSpans}</h1>
</div>`;

  const initLines: string[] = [];
  const tweenLines: string[] = [];
  for (let i = 0; i < words.length; i++) {
    const target = `#${clipPrefix}-w-${i}`;
    const localAt = i * STAGGER_SEC;
    const initFrom: Record<string, unknown> = { opacity: 0, y: 40 };
    const toProps: Record<string, unknown> = { opacity: 1, y: 0, duration: 0.73, ease: "raw:\"power3.out\"" };
    if (variant === "scale") {
      initFrom.scale = 0.7;
      toProps.scale = 1;
    } else if (variant === "blur") {
      initFrom.filter = "blur(14px)";
      toProps.filter = "blur(0px)";
    } else if (variant === "rotate") {
      initFrom.rotation = -6;
      toProps.rotation = 0;
    }
    initLines.push(`gsap.set(${JSON.stringify(target)}, ${stringifyProps(initFrom)});`);
    tweenLines.push(t.fromTo(target, initFrom, toProps, localAt));
  }

  tweenLines.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([
    backdrop,
    {
      html,
      init: initLines.join("\n"),
      tweens: tweenLines,
    },
  ]);
};

function stringifyProps(o: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(o)) {
    if (typeof v === "string" && v.startsWith("raw:")) {
      parts.push(`${JSON.stringify(k)}:${v.slice(4)}`);
    } else {
      parts.push(`${JSON.stringify(k)}:${JSON.stringify(v)}`);
    }
  }
  return `{${parts.join(",")}}`;
}

// Keep `tl` import bound so isolated builds don't tree-shake it away if helpers grow.
void tl;
